import React, { useState } from 'react'
import swal from 'sweetalert2'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useMe } from '../../../hooks/useMe'
import request from '../../../utils/fetcher'
import {
  resetConsumerAccess,
  changePreviousBox,
  setBankIdOverride,
  changeParcelLocation,
} from '../../../redux/parcels/async.parcelActions'
import showResponseText, { showProblem } from '../showResponseText'

// A parcel is only still going somewhere until it reaches one of these
const FINISHED_STATUSES = [
  'cancelled',
  'collected',
  'driver-picked-up',
  'pickup-collected',
]

const confirmAction = async (text) =>
  (
    await swal.fire({
      text,
      showCancelButton: true,
      showConfirmButton: true,
    })
  ).isConfirmed

// The registry shows this to iBoxen, which is the carrier half of every guard
// here. What is left is the key on the user's role (not `can`: the carrier
// half is already settled) and each action's own condition. An iBoxen user
// whose role carries none of the four, or whose parcel suits none, gets no
// card at all.
export default function RepairActions({ parcel, onChanged }) {
  const { t } = useTranslation('parcels')
  const { t: transResponseMessage } = useTranslation('responseMessages')
  const dispatch = useDispatch()
  const { user } = useMe()
  const { carrierHeader } = useSelector((state) => state.root)
  const [saving, setSaving] = useState(false)

  const { currentStatus } = parcel
  const showReset =
    user.isAllowedTo('resetConsumerAccess') && currentStatus === 'collected'
  const showPreviousBox =
    user.isAllowedTo('changePreviousBox') &&
    parcel.location?._id &&
    (FINISHED_STATUSES.includes(currentStatus) || parcel.isAllocated === false)
  const showBankIdOverride =
    user.isAllowedTo('overrideBankId') && currentStatus === 'in-box'
  const showChangeLocation = user.isAllowedTo('changeParcelLocation')

  if (
    ![showReset, showPreviousBox, showBankIdOverride, showChangeLocation].some(
      Boolean
    )
  ) {
    return null
  }

  // The page is reloaded even after an error: e.g. cleared signatures stay
  // cleared when the status call fails
  const runRepair = async (action) => {
    try {
      const { payload } = await dispatch(action)
      showResponseText(payload, transResponseMessage)
    } catch (e) {
      // the actions have already shown their own error when the request
      // failed; this is for anything thrown around them
      showProblem(transResponseMessage)
    } finally {
      onChanged()
    }
  }

  // From the first click until the write has come back, so a second click cannot open a
  // second prompt (or a second location read) on the same parcel.
  const guarded = (handler) => async () => {
    if (saving) return
    setSaving(true)
    try {
      await handler()
    } finally {
      setSaving(false)
    }
  }

  const resetAccess = async () => {
    const compartment = parcel.compartment || parcel.boxNumber || '-'
    if (
      !(await confirmAction(t('confirmResetConsumerAccess', { compartment })))
    )
      return
    await runRepair(resetConsumerAccess({ _id: parcel._id }))
  }

  const changePreviousLocker = async () => {
    const response = await request(
      'GET',
      '/carrier/locations',
      { ids: parcel.location._id, incAllocatedBoxes: true },
      { headers: { 'x-iboxen-carrier': carrierHeader } }
    ).catch(() => null)
    const location = response?.locations?.[0]
    const markOf = (box) =>
      location?.controllers?.find((c) => c.controllerId === box.controllerId)
        ?.mark || '?'
    const freeBoxes = (location?.boxes || [])
      .filter((box) => !box.parcelId && !box.bookingId)
      .sort((a, b) => markOf(a).localeCompare(markOf(b)) || a.port - b.port)
    if (!freeBoxes.length) {
      swal.fire({ icon: 'info', text: t('noFreeBoxes') })
      return
    }
    const { value: boxId } = await swal.fire({
      title: t('changePreviousBox'),
      text: t('changePreviousBoxInfo'),
      input: 'select',
      inputOptions: Object.fromEntries(
        freeBoxes.map((box) => [box._id, `${markOf(box)} – ${box.port + 1}`])
      ),
      showCancelButton: true,
    })
    if (boxId) await runRepair(changePreviousBox({ _id: parcel._id, boxId }))
  }

  const toggleBankIdOverride = async () => {
    const enabled = !parcel.isBankIdOverride
    if (enabled && !(await confirmAction(t('confirmBankIdOff')))) return
    await runRepair(setBankIdOverride({ _id: parcel._id, enabled }))
  }

  const changeLocation = async () => {
    const { value: locationNumber } = await swal.fire({
      title: t('changeLocation'),
      text: t('changeLocationInfo'),
      input: 'text',
      inputLabel: t('locationNumber'),
      inputValue: parcel.location?.locationNumber ?? '',
      showCancelButton: true,
      inputValidator: (value) =>
        String(value || '').trim() ? undefined : t('locationNumber'),
    })
    if (locationNumber) {
      await runRepair(changeParcelLocation({ _id: parcel._id, locationNumber }))
    }
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h6" align="center">
        {t('repairActions')}
      </Typography>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        useFlexGap
        flexWrap="wrap"
      >
        {showReset && (
          <Button
            variant="contained"
            color="iboxen"
            size="small"
            disabled={saving}
            onClick={guarded(resetAccess)}
          >
            {t('resetConsumerAccess')}
          </Button>
        )}
        {showBankIdOverride && (
          <Button
            variant="contained"
            color="iboxen"
            size="small"
            disabled={saving}
            onClick={guarded(toggleBankIdOverride)}
          >
            {parcel.isBankIdOverride
              ? t('bankIdOverrideOn')
              : t('bankIdOverrideOff')}
          </Button>
        )}
        {showPreviousBox && (
          <Button
            variant="contained"
            color="iboxen"
            size="small"
            disabled={saving}
            onClick={guarded(changePreviousLocker)}
          >
            {t('changePreviousBox')}
          </Button>
        )}
        {showChangeLocation && (
          <Button
            variant="contained"
            color="iboxen"
            size="small"
            disabled={saving}
            onClick={guarded(changeLocation)}
          >
            {t('changeLocation')}
          </Button>
        )}
      </Stack>
    </Stack>
  )
}
